import React from 'react';
import { Send, Loader2, RefreshCw, Monitor, Smartphone, Columns, LayoutList, Users, History, BarChart3, UserPlus, ExternalLink, Copy, Check, Sun, Moon, Eye, Settings2, ChevronDown, FileText, ClipboardList, Sparkles, Type, Eraser } from 'lucide-react';
import SortableHeaderButtons from './SortableHeaderButtons';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import MoodPicker from './MoodPicker';
import HourglassIcon from './HourglassIcon';
import { toast } from 'sonner';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { OfflineQueueTriggerButton } from './OfflineQueuePanel';
import DailyStreakChip from './DailyStreakChip';

const HeaderToolbar = ({
  currentMood,
  currentMoodIndex,
  setCurrentMoodIndex,
  favoriteMoods,
  setFavoriteMoods,
  setShowMoodInfo,
  textSizeLevel,
  cycleTextSize,
  textSizeLabels,
  forceDesktopView,
  toggleDesktopView,
  forceSingleColumn,
  toggleColumnLayout,
  readingWidth,
  setReadingWidth,
  onShowAllHistory,
  setShowShareModal,
  isSharing,
  shareCode,
  historyShareCode,
  onShowBatchCharting,
  userRole,
  user,
  onShowTeamCollab,
  autoShareActive,
  teamUnreadCount,
  onNextPatient,
  onGenerateDocs,
  isGenerating,
  // Feb 27, 2026 — `notes` is needed to disable header-generate-btn when
  // empty, matching all the other Generate buttons in the app.
  notes,
  onClearAll,
  onCopyAll,
  copyAllReady,
  // Feb 26, 2026 — Note-only and Orders-only copies on the top toolbar.
  // Surgical alternatives to Copy All when staff need just one or the other.
  onCopyNoteOnly,
  copyNoteReady,
  onCopyOrdersOnly,
  copyOrdersReady,
  // Feb 27, 2026 — Copy NOTE TITLE only. Useful when pasting a chart row
  // into a tracker/spreadsheet where you only want the title line (date +
  // patient + reason), not the full SOAP body.
  onCopyTitle,
  copyTitleReady,
  // Save draft to history+team — Feb 24, 2026: surfaced on the top toolbar
  // alongside Generate / Copy All so the user doesn't have to scroll to find
  // it in the input area or output panel.
  onSaveDraft,
  saveDraftReady,
  // Combined Sign+Copy button — Feb 24, 2026: top-bar combo that fires
  // both copy-all and "signed in Genesis" at once. Mirrors the stoplight
  // combo so the provider doesn't have to scroll to find it.
  onSignAndCopy,
  signAndCopyReady,
  patientThemeIds,
  onShowOfflineQueue,
}) => {
  const navigate = useNavigate();
  const [justCopied, setJustCopied] = React.useState(false);
  const [justCopiedNote, setJustCopiedNote] = React.useState(false);
  const [justCopiedOrders, setJustCopiedOrders] = React.useState(false);
  const [justCopiedTitle, setJustCopiedTitle] = React.useState(false);
  const [justSignedAndCopied, setJustSignedAndCopied] = React.useState(false);

  return (
    <div
      className="flex items-center gap-x-1 gap-y-1.5 px-2 py-1.5 bg-secondary/60 rounded-lg border border-border/50 relative z-20 backdrop-blur-sm flex-wrap overflow-x-auto"
      data-testid="header-toolbar"
      style={{ scrollbarWidth: 'thin' }}
    >
      {/* LEFT: Settings controls — Mood picker only for providers */}
      {userRole === 'provider' && (
        <MoodPicker currentMood={currentMood} currentMoodIndex={currentMoodIndex}
          setCurrentMoodIndex={setCurrentMoodIndex} favoriteMoods={favoriteMoods}
          setFavoriteMoods={setFavoriteMoods} setShowMoodInfo={setShowMoodInfo} />
      )}

      {/* Patient mode: Light / Dark / High Contrast toggle */}
      {userRole !== 'provider' && patientThemeIds && (
        <div className="flex items-center gap-0.5 bg-secondary/50 rounded-md p-0.5" data-testid="patient-theme-toggle">
          {[
            { id: 'clinical', label: 'Light', Icon: Sun },
            { id: 'calm', label: 'Dark', Icon: Moon },
            { id: 'high-contrast', label: 'Hi-Con', Icon: Eye },
          ].map(({ id, label, Icon }) => {
            const isActive = currentMood?.id === id;
            const themeIndex = patientThemeIds[id];
            return (
              <button key={id} onClick={() => { if (themeIndex !== undefined) setCurrentMoodIndex(themeIndex); }}
                className={`flex items-center gap-1 px-2.5 py-1.5 rounded text-xs font-medium transition-all ${
                  isActive
                    ? id === 'high-contrast' ? 'bg-blue-500 text-white shadow-sm' : 'bg-card text-primary shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
                data-testid={`theme-${id}-btn`}
              >
                <Icon className="w-3 h-3" />{label}
              </button>
            );
          })}
        </div>
      )}

      {/* Note: Feb 27, 2026 — "Next Tab" was moved to the far-right action
          cluster (next to Next Patient) per provider request — the new-tab
          and next-patient affordances are conceptually similar and now sit
          side-by-side instead of being separated by the entire toolbar. */}

      {/* View Settings — collapsed into popover to save space */}
      <Popover>
        <PopoverTrigger asChild>
          <Button variant="ghost" size="sm" className="h-7 px-1.5 gap-0.5 text-xs text-muted-foreground hover:text-foreground shrink-0" data-testid="view-settings-trigger" title="View settings">
            <Settings2 className="w-3.5 h-3.5" />
            <ChevronDown className="w-2.5 h-2.5" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-2 space-y-1" align="start">
          <Button variant="ghost" size="sm" onClick={cycleTextSize}
            className={`w-full justify-start h-7 px-2 gap-1.5 text-xs ${textSizeLevel > 0 ? 'text-primary' : 'text-muted-foreground'}`}
            data-testid="text-size-toggle">
            <span className="font-bold">Aa</span><span className="text-[10px]">{textSizeLabels[textSizeLevel]}</span>
          </Button>
          <Button variant="ghost" size="sm" onClick={toggleDesktopView}
            className={`w-full justify-start h-7 px-2 gap-1.5 text-xs ${forceDesktopView ? 'text-blue-400' : 'text-muted-foreground'}`}
            data-testid="view-toggle">
            {forceDesktopView ? <Monitor className="w-3.5 h-3.5" /> : <Smartphone className="w-3.5 h-3.5" />}
            <span>{forceDesktopView ? 'Desktop' : 'Mobile'}</span>
          </Button>
          <Button variant="ghost" size="sm" onClick={toggleColumnLayout}
            className={`w-full justify-start h-7 px-2 gap-1.5 text-xs ${forceSingleColumn ? 'text-purple-400' : 'text-muted-foreground'}`}
            data-testid="column-toggle">
            {forceSingleColumn ? <LayoutList className="w-3.5 h-3.5" /> : <Columns className="w-3.5 h-3.5" />}
            <span>{forceSingleColumn ? '1 Column' : '2 Columns'}</span>
          </Button>
          {/* Mar 2, 2026 — Reading width selector. Only relevant when the
              user is in 1-column mode (2-column uses a natural half-split
              from the grid so a maxWidth override would fight it). Three
              tiers: Narrow (reading-focus), Comfortable (balanced), Wide
              (max-viewport-fill for wide monitors). */}
          {forceSingleColumn && setReadingWidth && (
            <div className="pt-1 border-t border-border/40 space-y-0.5" data-testid="reading-width-selector">
              <div className="px-2 pt-1 pb-0.5 text-[9px] font-bold uppercase tracking-wider text-muted-foreground">
                Reading Width
              </div>
              {[
                { key: 'narrow',      label: 'Narrow',      hint: 'Focused reading' },
                { key: 'comfortable', label: 'Comfortable', hint: 'Balanced' },
                { key: 'wide',        label: 'Wide',        hint: 'Fill screen' },
              ].map((opt) => (
                <Button
                  key={opt.key}
                  variant="ghost"
                  size="sm"
                  onClick={() => setReadingWidth(opt.key)}
                  className={`w-full justify-start h-7 px-2 gap-1.5 text-xs ${
                    readingWidth === opt.key ? 'text-primary bg-primary/10' : 'text-muted-foreground'
                  }`}
                  data-testid={`reading-width-${opt.key}`}
                >
                  <span className="w-3.5 flex justify-center">
                    {readingWidth === opt.key ? '●' : '○'}
                  </span>
                  <span>{opt.label}</span>
                  <span className="text-[9px] opacity-60 ml-auto">{opt.hint}</span>
                </Button>
              ))}
            </div>
          )}
          {onShowBatchCharting && userRole === 'provider' && (
            <Button variant="ghost" size="sm" onClick={onShowBatchCharting}
              className="w-full justify-start h-7 px-2 gap-1.5 text-xs text-amber-500"
              data-testid="speed-chart-toolbar-btn">
              <RefreshCw className="w-3.5 h-3.5" /><span>Speed Chart</span>
            </Button>
          )}
        </PopoverContent>
      </Popover>

      {onShowAllHistory && (
        <Button variant="ghost" size="sm" onClick={onShowAllHistory}
          className="h-7 px-2 gap-1 text-xs text-muted-foreground hover:text-foreground shrink-0"
          data-testid="full-history-btn" title="See All History">
          <History className="w-3.5 h-3.5" /><span className="text-[10px]">History</span>
        </Button>
      )}

      {/* ⌘K hint button — discoverability for the command palette.
          v6.8.46 — hidden in patient mode (the command palette doesn't
          surface patient-mode commands and the pill clutters the
          letter-modal toolbar). */}
      {userRole !== 'patient' && (
        <button
          type="button"
          onClick={() => window.dispatchEvent(new CustomEvent('writeright:open-command-palette'))}
          className="hidden sm:inline-flex items-center gap-1 h-7 px-2 text-[10px] text-muted-foreground hover:text-amber-300 transition-colors border border-border/40 rounded shrink-0"
          title="Open Command Palette (⌘K / Ctrl+K)"
          data-testid="cmdk-hint-btn"
        >
          <kbd className="font-mono text-[9px] px-1 py-0.5 bg-secondary/40 rounded">⌘K</kbd>
          <span>Quick</span>
        </button>
      )}

      <div className="flex-1 min-w-1" />

      {/* Daily streak chip — silent at-a-glance progress (Feb 16 UX #12) */}
      <DailyStreakChip />

      {/* Offline Queue trigger — only renders when queueCount > 0 */}
      {onShowOfflineQueue && <OfflineQueueTriggerButton onClick={onShowOfflineQueue} />}

      {/* Next Tab — Feb 27, 2026 (revised): moved to FAR LEFT of the toolbar
          per provider request. Sometimes a chart is still generating and a
          new patient walks in — you need a fresh tab without waiting. This
          is the FIRST clickable button on the workflow strip so it's
          impossible to miss. */}
      {userRole === 'provider' && (
        <Button variant="ghost" size="sm"
          onClick={() => {
            try { window.dispatchEvent(new CustomEvent('writeright:new-tab')); } catch { /* ignore */ }
            window.open(window.location.href, '_self');
            toast.success('New tab opened!', { duration: 1500 });
          }}
          className="h-8 px-2.5 gap-1 text-xs text-green-400 border border-green-500/30 hover:bg-green-500/10 shrink-0"
          data-testid="header-new-tab-btn" title="Open new tab (keeps current work — use while generate is still running)">
          <ExternalLink className="w-3.5 h-3.5" /><span className="text-[10px]">Next Tab</span>
        </Button>
      )}

      {/* MIDDLE-LEFT: Clear + Next Patient + (small) Share/Team/Stats/Update.
          Demoted Feb 24, 2026 — Share & Team no longer occupy the prime far-right
          slot. They sit before the spacer so the rightmost trio is the trio that
          actually ships a chart (Save → Generate → Copy All). */}
      {userRole === 'provider' && onClearAll && (
        <Button
          variant="ghost"
          size="sm"
          onClick={onClearAll}
          className="h-8 px-3 gap-1.5 text-xs font-semibold rounded-lg text-red-400/80 hover:text-red-300 hover:bg-red-500/15 transition-all shrink-0 border border-red-500/20 hover:border-red-500/40 ml-1"
          data-testid="header-clear-btn"
          title="Clear all fields (confirmation required)"
        >
          <Eraser className="w-3.5 h-3.5" />
          <span>Clear</span>
        </Button>
      )}

      {/* Sortable action cluster (Feb 27, 2026, revised) — Stats + Share +
          Team. The Update button moved to the row above next to Sign-In so
          Clear has breathing room, and the Next Patient button is pinned
          right after Generate so the ship-a-chart → start-next workflow
          lives in one place. */}
      <SortableHeaderButtons
        storageKey="writeright_header_button_order"
        buttons={[
          ...(user ? [{
            id: 'stats',
            render: () => (
              <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')}
                className="h-7 px-2 gap-1 text-xs text-foreground/60 hover:text-foreground hover:bg-secondary/60 shrink-0"
                data-testid="header-dashboard-btn" title="Dashboard (long-press to drag)">
                <BarChart3 className="w-3.5 h-3.5" /><span className="text-[10px] hidden lg:inline">Stats</span>
              </Button>
            ),
          }] : []),
          {
            id: 'share',
            // v6.8.46 — Hide Share in patient mode (patients don't share charts to teams).
            render: () => userRole !== 'patient' ? (
              <Button variant="ghost" size="sm" onClick={() => setShowShareModal(true)} disabled={isSharing}
                className={`h-7 px-2 gap-1 text-xs shrink-0 ${
                  shareCode ? 'text-green-400 bg-green-500/10' :
                  historyShareCode ? 'text-blue-400 bg-blue-500/10' :
                  'text-muted-foreground hover:text-foreground'
                }`}
                data-testid="header-share-btn" title="Share to Team (long-press to drag)">
                {isSharing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                <span className="text-[10px] hidden lg:inline">Share</span>
              </Button>
            ) : null,
          },
          {
            id: 'team',
            // v6.8.46 — Hide Team in patient mode (no team-collab role for patients).
            render: () => userRole !== 'patient' ? (
              <Button variant="ghost" size="sm" onClick={() => onShowTeamCollab && onShowTeamCollab()}
                className="h-7 px-2 gap-1 text-xs text-foreground/70 hover:bg-primary/10 relative shrink-0 font-semibold"
                data-testid="header-team-btn" title="Team Board (long-press to drag)">
                <Users className="w-3.5 h-3.5" /><span className="text-[10px] hidden lg:inline">Team</span>
                {autoShareActive && (
                  <span className="absolute -bottom-0.5 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full bg-green-500" data-testid="auto-share-indicator" />
                )}
                {teamUnreadCount > 0 && (
                  <span className="absolute -top-1 -right-0.5 min-w-[14px] h-[14px] flex items-center justify-center rounded-full bg-blue-500 text-white text-[8px] font-bold px-0.5" data-testid="team-unread-badge">
                    {teamUnreadCount > 9 ? '9+' : teamUnreadCount}
                  </span>
                )}
              </Button>
            ) : null,
          },
        ]}
      />

      <div className="flex-1 min-w-1" />

      {/* FAR-RIGHT cluster (Feb 28, 2026) — Save / Generate / Copy trio /
          Next Patient / Sign+Copy wrapped in their own flex-wrap unit so
          they collapse to a 2nd row TOGETHER (right-justified) when the
          viewport is narrower than the full toolbar width. Without this
          grouping the buttons would either clip (no wrap on a single row)
          or fragment ungracefully across multiple rows.

          Provider report Feb 28 2026: "the header is not wrapping or the
          padding is too short" — Next Patient was getting cut off on
          ~1200px logical viewports. */}
      <div
        className="flex items-center gap-1 flex-wrap justify-end ml-auto min-w-0 max-w-full"
        data-testid="header-toolbar-ship-cluster"
      >
      {/* FAR RIGHT — the ship-a-chart trio (Save → Generate → Copy All).
          Feb 24, 2026: promoted to the rightmost spot per provider workflow. */}
      {userRole === 'provider' && onSaveDraft && saveDraftReady && (
        <Button
          variant="outline"
          size="sm"
          onClick={() => onSaveDraft && onSaveDraft()}
          className="h-8 px-3 gap-1.5 text-xs font-semibold rounded-lg border-green-500/50 text-green-500 hover:bg-green-500/10 transition-all shrink-0"
          data-testid="header-save-btn"
          title="Save current draft to History (and share with team if enabled)"
        >
          <img src="/emblem_icon.png" alt="" className="w-4 h-4 object-contain" />
          <span>Save</span>
        </Button>
      )}

      {userRole === 'provider' && (
        <Button
          variant={isGenerating ? "ghost" : "default"}
          size="sm"
          onClick={() => {
            // Feb 25, 2026 debug trace — surfaces which Generate button fired
            // when the user reports "Generate doesn't work" again. Keep this
            // log in for at least a release cycle.
            console.log('[Generate] header-generate-btn click fired');
            if (typeof onGenerateDocs === 'function') onGenerateDocs();
            else console.warn('[Generate] header-generate-btn — onGenerateDocs is not a function:', typeof onGenerateDocs);
          }}
          disabled={isGenerating || !notes?.trim()}
          className={`h-8 px-3 gap-1.5 text-xs font-semibold rounded-lg transition-all ${
            isGenerating
              ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30'
              : 'bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-500 hover:to-amber-600 text-white shadow-sm'
          }`}
          data-testid="header-generate-btn"
          title={isGenerating ? "Generating..." : (notes?.trim() ? "Generate" : "Type or dictate notes first")}
        >
          {isGenerating ? (
            <><HourglassIcon className="w-4 h-4 animate-pulse" /><span>Generating...</span></>
          ) : (
            <><img src="/emblem_icon.png" alt="" className="w-5 h-5 object-contain" /><span>Generate</span></>
          )}
        </Button>
      )}

      {/* Copy buttons — positioned BETWEEN Generate and Next Patient
          (Feb 27, 2026 v6.8.16) per provider report: "what happened to
          Copy All / Copy Note that used to sit b/w Generate and Next Pt?"
          They were rendering AFTER Next Patient AND hidden until a
          generation result existed — so a fresh page or post-Next-Patient
          state showed nothing. Now they always render in the expected
          slot; the buttons themselves stay disabled until there's
          something to copy (visual continuity > hiding empty controls). */}
      <Button
        variant="outline"
        size="sm"
        onClick={() => {
          if (!copyAllReady || !onCopyAll) return;
          onCopyAll();
          setJustCopied(true);
          setTimeout(() => setJustCopied(false), 2000);
        }}
        disabled={!copyAllReady}
        className={`h-8 px-3 gap-1.5 text-xs font-semibold rounded-lg transition-all shrink-0 ${
          !copyAllReady
            ? 'opacity-40 cursor-not-allowed border-border/40 text-muted-foreground'
            : justCopied
              ? 'border-green-500/60 text-green-400 bg-green-500/10'
              : 'border-primary/40 text-primary hover:bg-primary/15 hover:border-primary/60'
        }`}
        data-testid="header-copy-all-btn"
        title={!copyAllReady ? 'Generate a note first to enable Copy All' : 'Copy all generated documents to clipboard'}
      >
        {justCopied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
        <span>{justCopied ? 'Copied!' : 'Copy All'}</span>
      </Button>

      {/* Feb 28, 2026 (v6.8.39) — Note Only / Orders Only / Sign + Copy
          are PROVIDER-only clinical actions. Hide for patient role since
          patients are working with a single letter, not separate clinical
          note + orders documents. */}
      {userRole === 'provider' && onCopyNoteOnly && (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            if (!copyNoteReady) return;
            onCopyNoteOnly();
            setJustCopiedNote(true);
            setTimeout(() => setJustCopiedNote(false), 1800);
          }}
          disabled={!copyNoteReady}
          className={`h-8 px-2.5 gap-1 text-[11px] font-semibold rounded-lg transition-all shrink-0 ${
            !copyNoteReady
              ? 'opacity-40 cursor-not-allowed text-muted-foreground border border-border/40'
              : justCopiedNote
                ? 'text-emerald-300 bg-emerald-500/15 border border-emerald-500/40'
                : 'text-blue-300/85 hover:text-blue-200 hover:bg-blue-500/10 border border-blue-500/30'
          }`}
          data-testid="header-copy-note-btn"
          title={!copyNoteReady ? 'Generate a clinical note first' : 'Copy CLINICAL NOTE only (no orders, no ICD-10 block)'}
        >
          {justCopiedNote ? <Check className="w-3 h-3" /> : <FileText className="w-3 h-3" />}
          <span>{justCopiedNote ? 'Note ✓' : 'Note'}</span>
        </Button>
      )}

      {userRole === 'provider' && onCopyOrdersOnly && (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            if (!copyOrdersReady) return;
            onCopyOrdersOnly();
            setJustCopiedOrders(true);
            setTimeout(() => setJustCopiedOrders(false), 1800);
          }}
          disabled={!copyOrdersReady}
          className={`h-8 px-2.5 gap-1 text-[11px] font-semibold rounded-lg transition-all shrink-0 ${
            !copyOrdersReady
              ? 'opacity-40 cursor-not-allowed text-muted-foreground border border-border/40'
              : justCopiedOrders
                ? 'text-emerald-300 bg-emerald-500/15 border border-emerald-500/40'
                : 'text-amber-300/85 hover:text-amber-200 hover:bg-amber-500/10 border border-amber-500/30'
          }`}
          data-testid="header-copy-orders-btn"
          title={!copyOrdersReady ? 'Generate orders/referrals/ICD-10 first' : 'Copy ICD-10 codes, referrals & imaging orders only'}
        >
          {justCopiedOrders ? <Check className="w-3 h-3" /> : <ClipboardList className="w-3 h-3" />}
          <span>{justCopiedOrders ? 'Orders ✓' : 'Orders'}</span>
        </Button>
      )}

      {/* Next Patient — pinned right after the Copy trio (Feb 27, 2026 revised).
          Rich opaque emerald (was translucent gradient). Save current work
          and start next patient in one click. */}
      {userRole === 'provider' && onNextPatient && (
        <Button
          size="sm"
          onClick={onNextPatient}
          className="relative h-9 px-3.5 gap-1.5 text-xs font-bold rounded-lg border-2 border-emerald-300 bg-emerald-600 hover:bg-emerald-500 active:bg-emerald-700 text-white shadow-md shadow-emerald-700/40 hover:shadow-lg hover:shadow-emerald-500/50 hover:scale-105 active:scale-95 transition-all shrink-0"
          data-testid="header-next-patient-btn"
          title="Save current work and start next patient"
        >
          <Sparkles className="w-3 h-3 text-emerald-100 -mr-0.5" />
          <UserPlus className="w-4 h-4" />
          <span>Next Patient</span>
          <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-emerald-200 border border-white shadow shadow-emerald-300/80 animate-pulse" aria-hidden="true" />
        </Button>
      )}

      {/* Feb 28, 2026 (v6.8.39) — Patient parallel to Next Patient:
          "New Appt" starts a fresh letter / appointment without losing
          the saved work. Single button, no clinical SOAP/orders parity. */}
      {userRole !== 'provider' && onNextPatient && (
        <Button
          size="sm"
          onClick={onNextPatient}
          className="relative h-9 px-3.5 gap-1.5 text-xs font-bold rounded-lg border-2 border-sky-300 bg-sky-600 hover:bg-sky-500 active:bg-sky-700 text-white shadow-md shadow-sky-700/40 hover:shadow-lg hover:shadow-sky-500/50 hover:scale-105 active:scale-95 transition-all shrink-0"
          data-testid="header-new-appt-btn"
          title="Save current letter and start a new appointment letter"
        >
          <Sparkles className="w-3 h-3 text-sky-100 -mr-0.5" />
          <UserPlus className="w-4 h-4" />
          <span>New Appt</span>
        </Button>
      )}

      {/* Next Tab moved to FAR LEFT of toolbar (Feb 27, 2026 revised) —
          see the block above the Clear button. */}

      {/* (Copy All / Note / Orders moved ABOVE Next Patient — Feb 27, 2026 v6.8.16) */}

      {/* Sign + Copy combo button — top-bar shortcut. Same action as the
          stoplight "Copy & Sign" combo: fires copy-all AND marks the encounter
          as signed in Genesis in one click. Only shown when ready. */}
      {userRole === 'provider' && signAndCopyReady && onSignAndCopy && (
        <Button
          variant="default"
          size="sm"
          onClick={() => {
            onSignAndCopy();
            setJustSignedAndCopied(true);
            setTimeout(() => setJustSignedAndCopied(false), 2200);
          }}
          className={`h-8 px-3 gap-1.5 text-xs font-bold rounded-lg transition-all shrink-0 ${
            justSignedAndCopied
              ? 'bg-emerald-500/25 text-emerald-200 border border-emerald-500/60'
              : 'bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-500 hover:to-emerald-600 text-white border border-emerald-500/40 shadow-sm'
          }`}
          data-testid="header-sign-copy-btn"
          title="Copy all + mark as Signed in Genesis (one click)"
        >
          {justSignedAndCopied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
          <span>{justSignedAndCopied ? 'Signed!' : 'Sign + Copy'}</span>
        </Button>
      )}
      </div>
    </div>
  );
};

export default HeaderToolbar;
